import { LoadStatus } from '@rx-applications/shared/utils';
import { ReportingConfiguration } from './reporting-configuration';

export const MealOptions = {
  AnyMealOption: 0,
  AllBreakfastOptions: 14,
  IncludesDinner: 99,
};

type MealOptionValue = (typeof MealOptions)[keyof typeof MealOptions];

export interface CompanyConfiguration {
  name: string;
  plan: string;
  monthlySpend: number;
  companyCreatedAt: string;
}

export enum PaymentOption {
  All = 'All',
  PayAdvance = 'PayAdvance',
  PayHotel = 'PayHotel',
}

export interface GeneralConfiguration {
  clientLogoUrl: string;
  firstName: string;
  lastName: string;
  fullName: string;
  emailAddress: string;
  phoneNumber: string;
  address1: string;
  address2: string;
  city: string;
  country: string;
  websiteGuid: string;
  websiteId: number;
  emergencyPhone: boolean;
  enableAnalytics: boolean;
  enableLoyaltyPrograms: boolean;
  enableProFormaStatements: boolean;
  customHomepage: {
    enableTemplate: boolean;
    templateName: 'corporate' | 'dashboard' | 'leisure';
  };
  defaultCurrency: string;
  hasTrainlineAccount: boolean;
  brand: string;
  company: CompanyConfiguration;
  userHash: string;
  userCreatedAt: string;
  enableRoomexPay?: boolean;
  allowCompanyPaymentPage: boolean;
  allowPayPortal?: boolean;
  displayUsTaxesAndFees?: boolean;
  hideDueAtCheckIn?: boolean;
  userRequiresApproval?: boolean;
  requireApprovalForOutOfPolicyOnly?: boolean;
  approvers?: Approver[];
  approvalFlowEnabled: boolean;
}

export interface SearchConfiguration {
  selectedCurrency: string;
  isCurrencyInitialize?: boolean;
  showPaymentOptions: boolean;
  defaultPaymentOption: PaymentOption;
  showMealOptions: boolean;
  defaultMealOption: MealOptionValue;
  policyEnabled: boolean;
  hideInPolicy: boolean;
  topLocations?: TopRegion[];
  numberRecommendedHotels: number;
  recentlyBooked: RecentlyBookedHotel[];
  recommendLocationHeader: string;
  roomSelectSortBy: RoomSelectSortBy;
}

export interface AutocompleteRegion {
  heading: string;
  options: {
    LocationName: string;
    LocationCode: string;
    LocationUrl: string;
  }[];
}

export interface TopRegion {
  locations: TopLocation[];
  region: string;
  code?: string;
}

export interface TopLocation {
  name: string;
  url: string;
  code?: string;
}

export interface RecentlyBookedHotel {
  name: string;
  url: string;
  id: number;
  thumbnailUrl: string;
}

export interface ReservationConfiguration {
  allowPayments: boolean;
  allowAutoCancel: boolean;
  disableMailingList: boolean;
  disableRepeatLink: boolean;
  showLogoOnReservationConfirmation: boolean;
  showClientFieldsInCustomerDetails: boolean;
  companyName: string;
  companyNumber: number;
  displayRoomexCompanyDetails: boolean;
  displayInvoiceNumber: boolean;
  enableMixedSupplierResults: boolean;
  displayOutofHoursPhoneNumber: boolean;
  outofHoursPhoneNumber: string;
  enablePaxPredictive: boolean;
  paymentMethods?: {
    allowSaveCreditCard: boolean;
    onAccount: boolean;
    creditCard: boolean;
  };
}

export interface UserConfiguration {
  general: GeneralConfiguration;
  search: SearchConfiguration;
  reservation: ReservationConfiguration;
  reports: ReportingConfiguration;
}

export interface User {
  loadStatus: LoadStatus;
  configuration: UserConfiguration;
}

export interface Approver {
  username: string;
}

export type RoomSelectSortBy = 'price' | 'refundableStatus';
